import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import {
  levelOutSchema,
  levelRemovalOutSchema,
  teamOutSchema,
  type LevelBatchIn,
  type LevelIn,
  type LevelOut,
  type LevelRemovalOut,
  type StructureOut,
  type TeamAssignmentIn,
  type TeamIn,
  type TeamOut,
  type TeamTrackedAccountRow,
  type TeamUpdateIn,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export const STRUCTURE_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'structure'] as const;

export const TEAM_ACCOUNTS_ROOT_QUERY_KEY = (campaignId: string) =>
  ['campaign', campaignId, 'team-accounts'] as const;

export const TEAM_ACCOUNTS_QUERY_KEY = (campaignId: string, teamId: string) =>
  [...TEAM_ACCOUNTS_ROOT_QUERY_KEY(campaignId), teamId] as const;

export const TEAM_ACCOUNT_SEARCH_QUERY_KEY = (campaignId: string, q: string) =>
  [...TEAM_ACCOUNTS_ROOT_QUERY_KEY(campaignId), 'search', q] as const;

/** A team's direct accounts (`GET .../tracked-accounts?team_id=<id>`), for `TeamAccounts`'s
 *  "Vinculadas" list. `open` mirrors the dialog: `TeamActions` mounts one `TeamAccounts` per
 *  team unconditionally, so without this gate every rendered card fires its own request. */
export function useTeamAccounts(
  campaignId: string,
  teamId: string,
  open: boolean,
): UseQueryResult<TeamTrackedAccountRow[], ApiFailureError> {
  const client = useApiClient();

  return useQuery<TeamTrackedAccountRow[], ApiFailureError>({
    queryKey: TEAM_ACCOUNTS_QUERY_KEY(campaignId, teamId),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.teamTrackedAccounts, { campaignId }, { team_id: teamId }),
        GET_ROUTES.teamTrackedAccounts.schema,
      );
      return items;
    },
    enabled: open && campaignId !== '' && teamId !== '',
  });
}

/** `GET .../tracked-accounts?q=<text>`, carrying `team_id` so `TeamAccounts` can label a
 *  candidate already in another team and exclude one already in this one. `open` gates it the
 *  same way `useTeamAccounts` is gated: a stale non-empty `query` left over from a previous
 *  open must not keep searching once the dialog is closed. */
export function useTeamAccountSearch(
  campaignId: string,
  q: string,
  open: boolean,
): UseQueryResult<TeamTrackedAccountRow[], ApiFailureError> {
  const client = useApiClient();
  const trimmed = q.trim();

  return useQuery<TeamTrackedAccountRow[], ApiFailureError>({
    queryKey: TEAM_ACCOUNT_SEARCH_QUERY_KEY(campaignId, trimmed),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.teamTrackedAccounts, { campaignId }, { q: trimmed }),
        GET_ROUTES.teamTrackedAccounts.schema,
      );
      return items;
    },
    enabled: open && campaignId !== '' && trimmed !== '',
  });
}

export function useStructure(campaignId: string): UseQueryResult<StructureOut, ApiFailureError> {
  const client = useApiClient();

  return useQuery<StructureOut, ApiFailureError>({
    queryKey: STRUCTURE_QUERY_KEY(campaignId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.campaignStructure, { campaignId }),
        GET_ROUTES.campaignStructure.schema,
      ),
    enabled: campaignId !== '',
    staleTime: Infinity,
  });
}

export function useLevelCreate(
  campaignId: string,
): UseMutationResult<LevelOut, ApiFailureError, LevelIn> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<LevelOut, ApiFailureError, LevelIn>({
    mutationFn: (body) =>
      client.postParsed(`/campaigns/${campaignId}/levels`, body, levelOutSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STRUCTURE_QUERY_KEY(campaignId),
      });
    },
  });
}

export type RenameLevelArgs = {
  levelId: string;
  name: string;
};

export function useRenameLevel(
  campaignId: string,
): UseMutationResult<LevelOut, ApiFailureError, RenameLevelArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<LevelOut, ApiFailureError, RenameLevelArgs>({
    mutationFn: ({ levelId, name }) =>
      client.putParsed(`/campaigns/${campaignId}/levels/${levelId}`, { name }, levelOutSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STRUCTURE_QUERY_KEY(campaignId),
      });
    },
  });
}

export type LevelRemovalArgs = { levelId: string } | string;

function resolveLevelId(args: LevelRemovalArgs): string {
  return typeof args === 'string' ? args : args.levelId;
}

export function usePreviewLevelRemoval(
  campaignId: string,
): UseMutationResult<LevelRemovalOut, ApiFailureError, LevelRemovalArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<LevelRemovalOut, ApiFailureError, LevelRemovalArgs>({
    mutationFn: (args) => {
      const levelId = resolveLevelId(args);
      return client.delParsed(
        `/campaigns/${campaignId}/levels/${levelId}?preview=true`,
        levelRemovalOutSchema,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STRUCTURE_QUERY_KEY(campaignId),
      });
    },
  });
}

export function useRemoveLevel(
  campaignId: string,
): UseMutationResult<LevelRemovalOut, ApiFailureError, LevelRemovalArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<LevelRemovalOut, ApiFailureError, LevelRemovalArgs>({
    mutationFn: (args) => {
      const levelId = resolveLevelId(args);
      return client.delParsed(`/campaigns/${campaignId}/levels/${levelId}`, levelRemovalOutSchema);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STRUCTURE_QUERY_KEY(campaignId),
      });
    },
  });
}

/** `?preview=true` on the batch route: computes the consequence of the whole staged level
 *  list without writing it. Never invalidates the structure query -- it changes nothing. */
export function usePreviewStructure(
  campaignId: string,
): UseMutationResult<LevelRemovalOut, ApiFailureError, LevelBatchIn> {
  const client = useApiClient();

  return useMutation<LevelRemovalOut, ApiFailureError, LevelBatchIn>({
    mutationFn: (body) =>
      client.putParsed(`/campaigns/${campaignId}/levels?preview=true`, body, levelRemovalOutSchema),
  });
}

/** The batch route's real write: applies the ordered level list in one transaction. */
export function useSaveStructure(
  campaignId: string,
): UseMutationResult<LevelRemovalOut, ApiFailureError, LevelBatchIn> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<LevelRemovalOut, ApiFailureError, LevelBatchIn>({
    mutationFn: (body) =>
      client.putParsed(`/campaigns/${campaignId}/levels`, body, levelRemovalOutSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STRUCTURE_QUERY_KEY(campaignId),
      });
    },
  });
}

export function useTeamCreate(
  campaignId: string,
): UseMutationResult<TeamOut, ApiFailureError, TeamIn> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<TeamOut, ApiFailureError, TeamIn>({
    mutationFn: (body) => client.postParsed(`/campaigns/${campaignId}/teams`, body, teamOutSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STRUCTURE_QUERY_KEY(campaignId),
      });
    },
  });
}

export interface TeamUpdateArgs {
  teamId: string;
  body: TeamUpdateIn;
}

export function useTeamUpdate(
  campaignId: string,
): UseMutationResult<TeamOut, ApiFailureError, TeamUpdateArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<TeamOut, ApiFailureError, TeamUpdateArgs>({
    mutationFn: ({ teamId, body }) =>
      client.patchParsed(`/campaigns/${campaignId}/teams/${teamId}`, body, teamOutSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: STRUCTURE_QUERY_KEY(campaignId),
      });
    },
  });
}

export function useTeamDelete(
  campaignId: string,
): UseMutationResult<void, ApiFailureError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, string>({
    mutationFn: (teamId) => client.del(`/campaigns/${campaignId}/teams/${teamId}`),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STRUCTURE_QUERY_KEY(campaignId) }),
        queryClient.invalidateQueries({ queryKey: TEAM_ACCOUNTS_ROOT_QUERY_KEY(campaignId) }),
      ]);
    },
  });
}

export type MoveAccountArgs = {
  trackedAccountId: string;
  team_id?: string | null;
  teamId?: string | null;
};

export function useMoveAccount(
  campaignId: string,
): UseMutationResult<void, ApiFailureError, MoveAccountArgs> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiFailureError, MoveAccountArgs>({
    mutationFn: async ({ trackedAccountId, team_id, teamId }) => {
      const targetTeamId = team_id !== undefined ? team_id : (teamId ?? null);
      const body: TeamAssignmentIn = { team_id: targetTeamId };
      await client.putParsed(
        `/campaigns/${campaignId}/tracked-accounts/${trackedAccountId}/team`,
        body,
        z.undefined(),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STRUCTURE_QUERY_KEY(campaignId) }),
        queryClient.invalidateQueries({ queryKey: TEAM_ACCOUNTS_ROOT_QUERY_KEY(campaignId) }),
      ]);
    },
  });
}
